import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ModulePaymentDialog from './ModulePaymentDialog';

// UI Components
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';

// Icons
import {
  BookOpen,
  ClipboardList,
  Calendar,
  MessageSquare,
  Heart,
  CreditCard,
  Utensils,
  Bus,
  FileText,
  FolderOpen,
  CheckCircle2,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModuleSubscriptionDialog = ({ open, onOpenChange, tenantModules, optionalModules, onSuccess }) => {
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingModules, setFetchingModules] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const moduleIcons = {
    grades: BookOpen,
    attendance: ClipboardList,
    timetable: Calendar,
    messaging: MessageSquare,
    school_life: Heart,
    billing: CreditCard,
    canteen: Utensils,
    transport: Bus,
    reports: FileText,
    documents: FolderOpen
  };

  useEffect(() => {
    if (open) {
      fetchAvailableModules();
    }
  }, [open, tenantModules, optionalModules]);

  const fetchAvailableModules = async () => {
    try {
      setFetchingModules(true);
      
      const token = localStorage.getItem('access_token');
      // Récupérer tous les modules disponibles avec leurs prix (endpoint pour admins de tenant)
      const response = await axios.get(`${API}/tenant/modules/available-pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filtrer pour ne garder que les modules payants (non base modules)
      const paidModules = response.data.filter(module => !module.is_base_module);

      // Filtrer pour ne montrer que les modules non encore souscrits
      // Combiner les modules de base ET les modules optionnels actifs
      const subscribedModuleIds = [
        ...(tenantModules?.map(m => m.module_id) || []),
        ...(optionalModules?.map(m => m.module_id) || [])
      ];
      
      const unsubscribedModules = paidModules.filter(
        module => !subscribedModuleIds.includes(module.module_id)
      );

      setAvailableModules(unsubscribedModules);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Erreur lors du chargement des modules disponibles');
    } finally {
      setFetchingModules(false);
    }
  };

  const handleModuleToggle = (moduleId) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handleSubscribe = () => {
    if (selectedModules.length === 0) {
      toast.error('Veuillez sélectionner au moins un module');
      return;
    }
    
    // Ouvrir le dialogue de paiement
    setPaymentDialogOpen(true);
  };
  
  const handlePaymentSuccess = () => {
    setSelectedModules([]);
    setPaymentDialogOpen(false);
    onOpenChange(false);
    
    // Émettre un événement pour forcer le rafraîchissement des modules
    window.dispatchEvent(new CustomEvent('modulesChanged'));
    
    if (onSuccess) {
      onSuccess();
    }
  };

  const calculateTotal = () => {
    return selectedModules.reduce((total, moduleId) => {
      const module = availableModules.find(m => m.module_id === moduleId);
      return total + (module?.monthly_price || 0);
    }, 0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const getModuleIcon = (moduleId) => {
    const IconComponent = moduleIcons[moduleId];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <FolderOpen className="w-5 h-5" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Souscrire aux Modules</DialogTitle>
          <DialogDescription>
            Sélectionnez les modules que vous souhaitez activer pour votre établissement
          </DialogDescription>
        </DialogHeader>

        {fetchingModules ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Chargement des modules...</span>
          </div>
        ) : availableModules.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Tous les modules sont déjà activés
            </h3>
            <p className="text-gray-600">
              Vous avez souscrit à tous les modules disponibles
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableModules.map((module) => {
                const isSelected = selectedModules.includes(module.module_id);
                
                return (
                  <Card
                    key={module.module_id}
                    className={`cursor-pointer transition-all ${
                      isSelected
                        ? 'ring-2 ring-blue-500 shadow-lg bg-blue-50'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => handleModuleToggle(module.module_id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                            {getModuleIcon(module.module_id)}
                          </div>
                          <div>
                            <CardTitle className="text-base">{module.name}</CardTitle>
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-blue-600">
                                {formatPrice(module.monthly_price)} GNF/mois
                              </div>
                              {module.has_custom_price && module.default_price && (
                                <div className="text-xs text-gray-500">
                                  <span className="line-through">{formatPrice(module.default_price)} GNF</span>
                                  <span className="ml-1 text-green-600 font-medium">• Tarif négocié</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleModuleToggle(module.module_id)}
                          className="mt-1"
                        />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600">
                        {module.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Résumé et bouton de confirmation */}
            {selectedModules.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-gray-600">
                        {selectedModules.length} module(s) sélectionné(s)
                      </div>
                      <div className="text-2xl font-bold text-blue-600 mt-1">
                        {formatPrice(calculateTotal())} GNF/mois
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSubscribe}
                    disabled={loading || selectedModules.length === 0}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Traitement...
                      </>
                    ) : (
                      `Souscrire (${formatPrice(calculateTotal())} GNF/mois)`
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
      
      {/* Dialogue de paiement */}
      <ModulePaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        selectedModules={selectedModules}
        totalAmount={calculateTotal()}
        onSuccess={handlePaymentSuccess}
      />
    </Dialog>
  );
};

export default ModuleSubscriptionDialog;
